
'use client';

import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CountPill } from '@/components/ui/count-pill';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollArea } from '../ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { showProgressToast } from '@/components/ui/progress-toast';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { isValidUrl } from '@/lib/url-validation';
import { googleBuyerNote } from '@/lib/google-copy-link';
import { formatFileSize } from '@/lib/product-file-pool';
import { useAuth } from '@/context/auth-context';
import { auth as firebaseAuth } from '@/firebase/client';

type FileItem = {
  name: string;
  /** Absent on legacy/tier-scoped file records that never stored a byte count. */
  size?: number;
  type?: string;
  url?: string;
};

/**
 * The server's answer to "what may this buyer download from this product".
 * `accessLinks` is the only way a buyer receives the product's paid links: they
 * live in the seller-only products/{id}/private/delivery, and this request is
 * where /api/download checks the buyer may have them.
 */
type Manifest = { files: FileItem[]; tierName: string; tierScoped: boolean; accessLinks: string[] };

/**
 * Resolved manifests for this tab, keyed by product id.
 *
 * A buyer holds exactly ONE entitlement per product, so their answer cannot
 * change while they are on the page — but the dialog is remounted on every
 * visit to it, and React state does not survive that. Without this, "checking
 * your access" ran again on every mount for a list that was already known.
 * Module scope, so it is per browser tab and dies with it: no cross-session
 * staleness, and a tier change (which reloads through the subscription flow)
 * lands on a fresh page anyway.
 */
const manifestCache = new Map<string, Manifest>();

type DownloadDialogProps = {
  /**
   * The caller's own answer to "what can this buyer download", used until the
   * server's manifest lands and as the fallback if that request fails. For a
   * tiered product the caller is expected to have scoped it already
   * (downloadCandidates in src/lib/tier-entitlement.ts).
   */
  files: FileItem[];
  /**
   * How many access links the product delivers, read off its public document
   * (accessLinkCount in src/lib/product-access-links.ts). The links themselves
   * arrive with the manifest; this count is what lets the dialog say they are
   * coming, or that they failed to load, rather than show nothing.
   */
  accessLinkCount?: number;
  productId?: string;
  /**
   * Whether the dialog is open. The manifest is fetched on the first open
   * rather than on mount: this component is rendered by the product page for
   * every owner on every visit, and Radix only renders its CONTENT when open —
   * the component function (and its effects) run either way. Fetching on mount
   * would mean one request per page view for a dialog most buyers never open.
   */
  open?: boolean;
  /** True for `pricingModel: 'tiered'` products — drives the scope note only. */
  isTiered?: boolean;
  /**
   * Whether `files` is already narrowed to the buyer's tier.
   *
   * False means the caller had no tier to scope by and handed over the whole
   * pool (see tierScopeResolved on the product page). That list is not an
   * answer, it is the absence of one, so the dialog must not render it: it
   * would show a tiered buyer every file on the product and let them start
   * downloads the server will refuse. Those cases wait for the manifest and
   * show an error if it never lands. A non-tiered product is always resolved.
   */
  tierScopeResolved?: boolean;
  /**
   * Name of the tier this buyer purchased, when it is known and the list is
   * actually narrowed to it. Empty for a legacy order with no recorded tier
   * (which gets the whole pool), for a retired tier the seller has since
   * deleted, and for a guest before the manifest resolves.
   */
  tierName?: string;
  /** True if the viewer is recovering via guest-purchase flow (no Firestore
   * user doc, or unverified email). Used only for messaging copy — all
   * enforcement now happens server-side in /api/download. */
  isGuest?: boolean;
  /** Guest buyer email, shown in the "sign up with this email" nudge. */
  guestEmail?: string;
  /** Called after the download loop finishes (success or partial failure). */
  onComplete?: () => void;
  /**
   * A bundle's products (src/lib/product-bundles.ts), each named with its tier
   * in brackets. Each one's files are on its own page, which the buyer now owns,
   * so the dialog links there; the files listed here are the bundle's bonus
   * files, if it has any.
   */
  bundleItems?: ReadonlyArray<{ productId: string; label: string }>;
};

const getFileType = (file: FileItem): string => {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext) return '—';
  const map: Record<string, string> = {
    zip: 'ZIP', rar: 'RAR', '7z': '7Z', tar: 'TAR', gz: 'GZ',
    pdf: 'PDF', doc: 'DOC', docx: 'DOCX', txt: 'TXT', csv: 'CSV', xlsx: 'XLSX', xls: 'XLS', pptx: 'PPTX',
    jpg: 'JPEG', jpeg: 'JPEG', png: 'PNG', gif: 'GIF', webp: 'WebP', svg: 'SVG', psd: 'PSD', ai: 'AI',
    mp4: 'MP4', mov: 'MOV', avi: 'AVI', mkv: 'MKV', webm: 'WebM',
    mp3: 'MP3', wav: 'WAV', flac: 'FLAC', aac: 'AAC',
    exe: 'EXE', dmg: 'DMG', apk: 'APK',
    js: 'JS', ts: 'TS', jsx: 'JSX', tsx: 'TSX', py: 'Python', rb: 'Ruby', java: 'Java',
    figma: 'Figma', sketch: 'Sketch', xd: 'Adobe XD',
  };
  return map[ext] || ext.toUpperCase();
};

/** Render a Retry-After (in seconds) as a user-friendly relative phrase.
 *  The new daily caps return retry windows up to ~24h, so the old "in 86400s"
 *  format would be unreadable. Bucketed at second/minute/hour resolution. */
function humanizeRetry(secs: number): string {
  if (!Number.isFinite(secs) || secs <= 0) return 'a moment';
  if (secs < 60) return `${Math.ceil(secs)} second${secs === 1 ? '' : 's'}`;
  if (secs < 3600) {
    const m = Math.ceil(secs / 60);
    return `${m} minute${m === 1 ? '' : 's'}`;
  }
  const h = Math.ceil(secs / 3600);
  return `${h} hour${h === 1 ? '' : 's'}`;
}

/**
 * Auth headers for a POST to /api/download.
 *
 * Authenticated request: attach a fresh Firebase ID token. Guests rely on the
 * httpOnly `guestDlToken` cookie (sent automatically with credentials).
 *
 * NOTE: useAuth()'s authUser is a spread copy ({ ...currentAuthUser }) from
 * auth-context.tsx, which drops prototype methods — getIdToken is missing. Pull
 * the real Firebase User off the SDK instance instead.
 *
 * In the guest flow the caller may ALSO be signed in (anonymous, or a Firebase
 * account that doesn't own this product). The server prefers Bearer over
 * cookie, so sending one would take the authed ownership path and 403. Skip
 * Bearer for guests; rely on the cookie.
 */
async function buildAuthHeaders(isGuest: boolean): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const realUser = firebaseAuth.currentUser;
  if (realUser && !isGuest) {
    try {
      headers.Authorization = `Bearer ${await realUser.getIdToken()}`;
    } catch (e) {
      console.error('[DownloadDialog] getIdToken failed, falling back to cookie:', e);
    }
  }
  return headers;
}

/** Triggers a real browser download. The URL is a short-lived signed Cloud
 *  Storage link (src/lib/download-link.ts) that carries
 *  `response-content-disposition=attachment`, so the browser saves the file
 *  and the current tab stays where it is. The link is cross-origin, so the
 *  browser ignores the `download` attribute and takes the filename from that
 *  header; the attribute is kept only as a harmless hint. */
function triggerDownload(downloadUrl: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = fileName;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function DownloadDialog({
  files,
  accessLinkCount = 0,
  productId,
  open = false,
  isTiered = false,
  tierName = '',
  tierScopeResolved = true,
  isGuest = false,
  guestEmail,
  onComplete,
  bundleItems = [],
}: DownloadDialogProps) {
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const { authUser } = useAuth();

  // The server's own answer to what this buyer may download. Seeded from the
  // process-wide cache so a product resolved once this session opens instantly
  // for the rest of it — see manifestCache.
  const [manifest, setManifest] = useState<Manifest | null>(
    () => (productId ? manifestCache.get(productId) ?? null : null),
  );
  const [fetching, setFetching] = useState(false);
  const [manifestFailed, setManifestFailed] = useState(false);
  // Why the manifest was refused, which decides what the access links section
  // says. Only a failure the server did not explain is worth a retry: an expired
  // guest session (401) needs a new access link, an ended subscription (403
  // access_expired) or a product this account does not own (403) will answer
  // the same way again.
  const [manifestRefusal, setManifestRefusal] = useState<'session' | 'ended' | 'disputed' | 'denied' | null>(null);
  const manifestRequestedRef = useRef(false);

  // Whether the buyer is looking at a real answer right now, from either side.
  // The caller's list counts: for an authed buyer the page scopes it with the
  // same function the server gates on, off data it already holds, so there is
  // nothing to wait for. Only a caller that could NOT scope (a guest on a
  // tiered product) genuinely depends on the round trip.
  const awaitingScope = !manifest && !tierScopeResolved;

  const runManifest = useCallback(async (): Promise<void> => {
    if (!productId) return;
    setFetching(true);
    setManifestFailed(false);
    setManifestRefusal(null);
    try {
      const res = await fetch('/api/download', {
        method: 'POST',
        headers: await buildAuthHeaders(isGuest),
        credentials: 'include',
        body: JSON.stringify({ productId, action: 'manifest' }),
      });
      if (!res.ok) {
        if (res.status === 401) setManifestRefusal('session');
        if (res.status === 403) {
          let reason: unknown;
          try { reason = (await res.json() as { reason?: unknown }).reason; } catch { /* not JSON */ }
          setManifestRefusal(reason === 'access_expired' ? 'ended' : reason === 'dispute_suspended' ? 'disputed' : 'denied');
        }
        throw new Error(`manifest ${res.status}`);
      }
      const body = await res.json() as { files?: FileItem[]; tierName?: string; tierScoped?: boolean; accessLinks?: unknown };
      if (!Array.isArray(body.files)) throw new Error('manifest malformed');
      const resolved: Manifest = {
        files: body.files,
        tierName: body.tierName ?? '',
        tierScoped: body.tierScoped === true,
        accessLinks: Array.isArray(body.accessLinks)
          ? body.accessLinks.filter((link): link is string => typeof link === 'string')
          : [],
      };
      manifestCache.set(productId, resolved);
      setManifest(resolved);
      // A file the buyer ticked before the manifest landed may not be on it.
      // Drop it rather than leave an invisible row in the selection count.
      setSelectedFiles(prev => prev.filter(name => resolved.files.some(f => f.name === name)));
    } catch (e) {
      // Two very different failures share this branch, and they must not share
      // a recovery. When the caller's list is already tier-scoped, the server
      // was only ever going to confirm it, so keep showing it. When it is NOT,
      // the caller handed over the whole pool — rendering that is how a tiered
      // buyer ends up looking at every file on the product. Show the failure
      // instead; `shownFiles` drops to empty below.
      console.error('[DownloadDialog] manifest fetch failed:', e);
      setManifestFailed(true);
      // Allow a later open to retry; a 500 here is usually transient.
      manifestRequestedRef.current = false;
    } finally {
      setFetching(false);
    }
  }, [productId, isGuest]);

  useEffect(() => {
    if (!open || !productId || manifest || manifestRequestedRef.current) return;
    manifestRequestedRef.current = true;
    void runManifest();
  }, [open, productId, manifest, runManifest]);

  const shownFiles = manifest?.files ?? (awaitingScope ? [] : files);
  const scopeTierName = manifest ? (manifest.tierScoped ? manifest.tierName : '') : tierName;
  // Only claim a narrowed list once it is actually narrowed — either the server
  // said so, or the caller scoped it to a tier it could name. Saying "your
  // tier's files" over an unscoped list would be a lie in the one direction
  // that matters.
  const showTierScopeNote = isTiered && (manifest ? manifest.tierScoped : !!tierName);
  // Blocking states, kept apart: still waiting on the only source that can
  // answer, versus that source having failed.
  // Covers the frame before the effect fires as well as the request itself —
  // `fetching` is still false then, and treating that as "not blocked" would
  // flash an empty dialog for a buyer who does have files.
  const blockedOnManifest = awaitingScope && !manifestFailed;
  const scopeUnavailable = awaitingScope && manifestFailed;

  // Purely for the "sign up to unlock" nudge copy — has no effect on limits.
  const isEmailUnlocked =
    isGuest &&
    !!guestEmail &&
    !!authUser?.email &&
    !!authUser?.emailVerified &&
    authUser.email.toLowerCase() === guestEmail.toLowerCase();

  const showGuestNudge = isGuest && !isEmailUnlocked;

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    if (checked === true) setSelectedFiles(shownFiles.map(f => f.name));
    else setSelectedFiles([]);
  };

  const handleSelectFile = (fileName: string, checked: boolean) => {
    if (checked) setSelectedFiles(prev => [...prev, fileName]);
    else setSelectedFiles(prev => prev.filter(name => name !== fileName));
  };

  /** Sentinel returned from requestDownloadUrl when the buyer has hit the
   *  per-buyer daily byte budget. Every subsequent file in the same batch
   *  will fail the same check, so the loop should break instead of stacking
   *  identical toasts. The per-file daily cap is NOT a batch-aborting
   *  failure — other files have their own counters. */
  type DownloadUrlResult = string | null | 'abort-batch';

  const requestDownloadUrl = async (fileName: string): Promise<DownloadUrlResult> => {
    const res = await fetch('/api/download', {
      method: 'POST',
      headers: await buildAuthHeaders(isGuest),
      credentials: 'include',
      body: JSON.stringify({ productId, fileName }),
    });

    if (!res.ok) {
      if (res.status === 429) {
        // Read the JSON body for a structured `reason` (file_daily_cap,
        // bytes_daily_cap) so the toast can explain *which* limit hit. Falls
        // back to the Retry-After header when the body isn't shaped that
        // way (e.g. the per-file 30s cooldown or per-IP burst limit, which
        // pre-date the daily caps).
        const retryHeaderSec = Number(res.headers.get('Retry-After') ?? '30');
        let reason: string | undefined;
        let serverMsg: string | undefined;
        try {
          const body = await res.json() as { error?: string; reason?: string };
          reason = body.reason;
          serverMsg = body.error;
        } catch { /* not JSON — fall through to defaults */ }

        if (reason === 'file_daily_cap') {
          toast({
            title: 'Daily download limit reached',
            description: `You can download "${fileName}" again in ${humanizeRetry(retryHeaderSec)}.`,
            variant: 'destructive',
          });
        } else if (reason === 'bytes_daily_cap') {
          toast({
            title: 'Daily bandwidth limit reached',
            description: `You've reached your 50GB daily download limit. Try again in ${humanizeRetry(retryHeaderSec)}.`,
            variant: 'destructive',
          });
          return 'abort-batch';
        } else {
          toast({
            title: 'Please wait',
            description: serverMsg ?? `You can download "${fileName}" again in ${retryHeaderSec}s.`,
            variant: 'destructive',
          });
        }
      } else if (res.status === 401) {
        toast({
          title: 'Could not download the file',
          description: 'Your access link or login has expired. Get a new link from the product page, or log in again.',
          variant: 'destructive',
        });
      } else if (res.status === 403) {
        // A canceled subscription whose access period has run out reports
        // `access_expired`, and an order under an open chargeback
        // `dispute_suspended`. Quote the server's explanation for those:
        // "your account doesn't have access" would leave a buyer with no idea
        // why a product they were downloading yesterday stopped working.
        let body: { error?: string; reason?: string } = {};
        try {
          body = await res.json() as typeof body;
        } catch { /* not JSON, fall through to the generic message */ }
        const explained = body.reason === 'access_expired' || body.reason === 'dispute_suspended';
        toast({
          title: body.reason === 'access_expired' ? 'Subscription ended'
            : body.reason === 'dispute_suspended' ? 'Access suspended' : 'Not authorised',
          description: explained && body.error
            ? body.error
            : "Your account doesn't have access to this file.",
          variant: 'destructive',
        });
      } else {
        // The route's 404 ("File no longer available") and 503 ("Could not
        // start the download") say something a buyer can act on; a bare status
        // code does not.
        let serverMsg: string | undefined;
        try {
          serverMsg = (await res.json() as { error?: string }).error;
        } catch { /* not JSON, fall through to the status code */ }
        toast({
          title: 'Could not download the file',
          description: serverMsg ? `${fileName}: ${serverMsg}` : `${fileName} didn't download. Try again in a moment.`,
          variant: 'destructive',
        });
      }
      return null;
    }

    const { url } = await res.json() as { url: string };
    return url;
  };

  const handleDownload = async () => {
    if (!productId || selectedFiles.length === 0) return;
    const picked = shownFiles.filter(f => selectedFiles.includes(f.name));
    setBusy(true);
    const progressToast = showProgressToast({
      title: 'Downloading files',
      status: `Preparing ${picked.length} file${picked.length > 1 ? 's' : ''}…`,
      progress: 0,
    });
    try {
      for (let i = 0; i < picked.length; i++) {
        const file = picked[i];
        progressToast.update({
          status: `Downloading ${file.name} (${i + 1} of ${picked.length})…`,
          progress: Math.round((i / picked.length) * 100),
        });
        // Serial so any 429 cooldown toast is legible, and so multiple
        // save-to-disk prompts don't stack up on top of each other.
        const downloadUrl = await requestDownloadUrl(file.name);
        if (downloadUrl === 'abort-batch') break;
        if (downloadUrl) triggerDownload(downloadUrl, file.name);
      }
      progressToast.update({ status: 'Finalising…', progress: 100 });
    } finally {
      progressToast.dismiss();
      setBusy(false);
      onComplete?.();
    }
  };

  const isAllSelected = selectedFiles.length > 0 && selectedFiles.length === shownFiles.length;
  const isSomeSelected = selectedFiles.length > 0 && !isAllSelected;
  const hasFiles = shownFiles.length > 0;
  // The links come from the manifest alone; the caller only knows how many to
  // expect. Unlike the file list there is no caller-side copy to show in the
  // meantime, so until the manifest lands they are loading, and if it fails
  // they are unavailable and the buyer is offered a retry.
  const accessLinks = manifest?.accessLinks ?? [];
  const hasAccessLinks = accessLinks.length > 0;
  const linksPending = !manifest && accessLinkCount > 0;
  const linksLoading = linksPending && !manifestFailed;
  const linksUnavailable = linksPending && manifestFailed;
  const expectsLinks = hasAccessLinks || linksPending;
  const totalFileSize = shownFiles.reduce((sum, f) => sum + (f.size || 0), 0);
  // A tiered buyer whose tier unlocks no files at all. Distinct from "this
  // product has no files": the product may well have plenty, just none on this
  // tier, and saying nothing at all would read as a broken dialog.
  const emptyTier = !hasFiles && !blockedOnManifest && !scopeUnavailable && showTierScopeNote;

  return (
    // One column allowed to shrink below its content: a long link's unbroken
    // width otherwise widened the grid past a phone's screen and carried the
    // link buttons, the centred title and Cancel off it.
    <DialogContent className="w-[calc(100vw-2rem)] max-w-5xl grid-cols-[minmax(0,1fr)]">
      <DialogHeader>
        <DialogTitle>Download files</DialogTitle>
        {(!expectsLinks || hasFiles) && !emptyTier && !blockedOnManifest && !scopeUnavailable && (
          <DialogDescription>Everything you bought is here.</DialogDescription>
        )}
        {blockedOnManifest && (
          <DialogDescription>Checking which files your purchase includes…</DialogDescription>
        )}
        {scopeUnavailable && (
          <DialogDescription>We couldn&apos;t confirm which files your purchase includes.</DialogDescription>
        )}
        {emptyTier && (
          <DialogDescription>
            {scopeTierName ? `You purchased the ${scopeTierName} tier.` : 'Your purchase is on one tier of this product.'}
          </DialogDescription>
        )}
        {showGuestNudge && (
          <p className="mt-2 text-xs text-muted-foreground">
            Guest downloads are limited to 1 per file every 30 seconds.{' '}
            <Link href="/signup" className="text-link underline decoration-link/40 underline-offset-4 hover:decoration-link">
              Sign up free
            </Link>{' '}
            with the same email you purchased with for unlimited re-downloads.
          </p>
        )}
      </DialogHeader>
      <div className="py-5 space-y-5">
        {bundleItems.length > 0 && (
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Products in this bundle
            </p>
            <p className="text-xs text-muted-foreground">
              Each one's files are on its own page, and they are all in your library.
            </p>
            <ScrollArea className="pr-6 rounded-lg border [&>[data-radix-scroll-area-viewport]]:max-h-72">
              <ul className="divide-y">
                {bundleItems.map((item) => (
                  <li key={item.productId} className="flex min-w-0 items-center justify-between gap-3 p-3">
                    <span className="min-w-0 truncate text-sm font-medium">{item.label}</span>
                    <Button asChild size="sm" variant="outline" className="shrink-0">
                      <Link href={`/product/${item.productId}`}>Get files</Link>
                    </Button>
                  </li>
                ))}
              </ul>
            </ScrollArea>
          </div>
        )}
        {expectsLinks && (
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Access links
            </p>
            {linksLoading && (
              <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                Checking your purchase before showing its links…
              </p>
            )}
            {linksUnavailable && (
              <div className="rounded-lg border border-dashed p-4 space-y-3">
                <p className="text-sm text-muted-foreground">
                  {manifestRefusal === 'session'
                    ? (isGuest
                      ? 'Your access link has expired. Request a new one from the product page to see these links.'
                      : 'Your session has expired. Log in again to see these links.')
                    : manifestRefusal === 'ended'
                      ? 'Your subscription has ended, so these links are no longer available.'
                      : manifestRefusal === 'disputed'
                        ? 'Access to this order is suspended while a payment dispute is open.'
                      : manifestRefusal === 'denied'
                        ? "This account doesn't have access to these links."
                        : "We couldn't load your access links. They're still part of your purchase."}
                </p>
                {/* Only a failure the server did not explain can be retried; the
                    tier block below offers the same retry when it is showing. */}
                {!manifestRefusal && !scopeUnavailable && (
                  <Button variant="outline" size="sm" onClick={() => void runManifest()} disabled={fetching}>
                    Try again
                  </Button>
                )}
              </div>
            )}
            {hasAccessLinks && (
              // As tall as its links, up to a cap past which it scrolls. A fixed
              // height left one link in a box built for three, and cut the
              // second link off once Google links carried a line of their own.
              <ScrollArea className="pr-6 rounded-lg border [&>[data-radix-scroll-area-viewport]]:max-h-72">
                <div className="p-4 space-y-2">
                  {accessLinks.map((link, i) => {
                    // Re-validate at render time. A malicious or compromised
                    // product doc could carry `javascript:` / `data:` URIs that
                    // would execute in the buyer's session if rendered raw. The
                    // edit-time validators run on save, but old/imported rows
                    // bypass them, so check here too.
                    const safe = isValidUrl(link);
                    // A Google file says what the link does: a "make a copy"
                    // link saves the buyer their own (src/lib/google-copy-link.ts).
                    const google = safe ? googleBuyerNote(link) : null;
                    return (
                      <div key={i} className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-sm truncate">{link}</p>
                          {google && <p className="text-xs text-muted-foreground">{google.note}</p>}
                        </div>
                        {safe ? (
                          <Button asChild variant="outline" size="sm">
                            <Link href={link} target="_blank" rel="noopener noreferrer">
                              {google?.makesCopy ? 'Make a copy' : 'Open link'}
                            </Link>
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" disabled title="Blocked unsafe link">
                            Unsafe link
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}
          </div>
        )}

        {emptyTier && (
          <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            {scopeTierName
              ? `The ${scopeTierName} tier doesn't include any downloadable files.`
              : "Your tier doesn't include any downloadable files."}
            {hasAccessLinks ? ' Use the access links above.' : ' Contact the seller if you expected files here.'}
          </p>
        )}

        {blockedOnManifest && (
          <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            Your purchase is on one tier of this product, and tiers can include different files.
          </p>
        )}

        {/* The list is withheld rather than shown unfiltered. Only the server
            knows which tier this purchase is on, so without it every file on
            the product would be offered — and most would then be refused. */}
        {scopeUnavailable && (
          <div className="rounded-lg border border-dashed p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Your files are safe. We just couldn&apos;t check which ones your tier includes.
            </p>
            <Button variant="outline" size="sm" onClick={() => void runManifest()} disabled={fetching}>
              Try again
            </Button>
          </div>
        )}

        {hasFiles && (
          <div className="space-y-2">
            <div className="flex items-baseline gap-2">
              <h4>Your files</h4>
              <span className="text-xs text-muted-foreground">
                {shownFiles.length} {shownFiles.length === 1 ? 'file' : 'files'}, {formatFileSize(totalFileSize)}
              </span>
            </div>
            {showTierScopeNote && (
              <p className="text-xs text-muted-foreground">
                {scopeTierName
                  ? `These are the files in your ${scopeTierName} tier.`
                  : 'These are the files in the tier you purchased.'}{' '}
                Other tiers of this product include different files.
              </p>
            )}
            <ScrollArea className={cn('pr-6 border rounded-lg', expectsLinks ? 'h-48' : 'h-60')}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        onCheckedChange={handleSelectAll}
                        checked={isAllSelected || (isSomeSelected ? 'indeterminate' : false)}
                      />
                    </TableHead>
                    <TableHead>Filename</TableHead>
                    <TableHead className="w-28 whitespace-nowrap">Size</TableHead>
                    <TableHead className="w-24 whitespace-nowrap">Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shownFiles.map((file, i) => {
                    const selected = selectedFiles.includes(file.name);
                    return (
                      <TableRow
                        key={i}
                        onClick={() => handleSelectFile(file.name, !selected)}
                        className={cn('cursor-pointer', selected && 'bg-accent/40')}
                      >
                        <TableCell>
                          <Checkbox
                            onCheckedChange={(checked) => handleSelectFile(file.name, !!checked)}
                            checked={selected}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="font-medium break-all">{file.name}</TableCell>
                        <TableCell className="whitespace-nowrap">{formatFileSize(file.size)}</TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">{getFileType(file)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}
      </div>

      {hasFiles && (
        <p className="border-t pt-4 text-xs text-muted-foreground">
          Files are provided by independent sellers.
          Download at your own risk.
        </p>
      )}

      <DialogFooter>
        <DialogClose asChild><Button variant="outline" disabled={busy}>Cancel</Button></DialogClose>
        {/* No manifest gate here any more. A rendered row is now only ever a
            row the buyer is entitled to: either the server said so, or the
            caller scoped the list to a tier it could name. The unscoped case
            renders no rows at all, so there is nothing to hold back. */}
        {hasFiles && (
          <Button onClick={handleDownload} disabled={selectedFiles.length === 0 || busy}>
            Download<CountPill count={selectedFiles.length} active />
          </Button>
        )}
      </DialogFooter>
    </DialogContent>
  );
}
